import React from 'react'
import { PageSpinner } from './PageSpinner'
import { Avatar } from '@mui/material'
import Image from 'next/image'
import { addCommaToNumber, checkIfDateIsAfter } from '@/utils'
import { useJob } from '@/features/job-seeker'
import relativeTime from 'dayjs/plugin/relativeTime'
import dayjs from 'dayjs'

dayjs.extend(relativeTime)

import { BsDot } from 'react-icons/bs'
import { LoginPopUp, ShareJob } from './Modals'
import Link from 'next/link'
import { Dislike, Share } from 'iconsax-react'
import { Tooltip } from '@mui/material'
import { useRouter } from 'next/router'
import { Button } from './Elements'

interface JobProps {
  jobId: string

  setOpenShareJob: (value: boolean) => void
  openShareJob: boolean
}

export const ExternalJobs: React.FC<JobProps> = ({
  jobId,
  setOpenShareJob,
  openShareJob,
  // openCompanyProfile,
  // setOpenCompanyProfile,
}) => {
  const router = useRouter()
  const [openLoginPopUp, setOpenLoginPopUp] = React.useState(false)
  const [openLoginPopUpCompanyProfile, setOpenLoginPopUpCompanyProfile] =
    React.useState(false)
  const { data, isLoading } = useJob(jobId)

  const {
    companyName,
    companyLocation,
    companyLogo,
    title,
    applicants,
    jobLocation,
    availableOpenings,
    datePosted,
    description,
    employmentType,
    level,
    summary,
    validThrough,
    experience,
    minQualification,
    salary,
    workType,
    isAnonymous,
    isEasyApply
  } = data?.data || {}

  const { currency, min, max, term, hidden } = salary || {}

  const formattedDeadline = dayjs(validThrough).fromNow()

  const handleOpenReportModal = () => setOpenLoginPopUp(true)
  const handleOpenShareModal = () => setOpenShareJob(true)
  const handleOpenCompanyProfile = () => setOpenLoginPopUpCompanyProfile(true)

  const isApplicationClosed = checkIfDateIsAfter(validThrough)

  const formattedPostedDate = dayjs(datePosted).fromNow()

  const locationArr = companyLocation?.split(',') || []
  const state = locationArr[locationArr.length - 2]
  const country = locationArr[locationArr.length - 1]

  if (isLoading) {
    return <PageSpinner className="mt-16" />
  }

  if (!data?.data) {
    router.push('/jobs')
    return null
  }

  const handleApplyForJob = () => {
    const companyNameNoSpace = companyName?.split(' ').join('')
    if (isEasyApply) {
      router.push(`/jobs/apply/${companyNameNoSpace}/${jobId}`)
    } else {
      router.push('/login')
    }
  }

  return (
    <>
 
      <div className="border-b bg-white shadow-lg sm:w-full">
        <div className="grid justify-items-center py-6">
          <Link href="/">
            <Image
              src="/assets/logo-black.png"
              alt="scout4Job-logo"
              className="cursor-pointer"
              height={41.65}
              width={162.05}
            />
          </Link>
        </div>
      </div>
      <div className="mx-auto mt-10 mb-8 max-w-[950px] border-b-4 border-[#16BF44] bg-white p-5 shadow-lg  sm:p-12">
        <div>
          <div className="flex flex-col justify-start gap-10 rounded-md transition duration-200 md:flex-row md:justify-between md:gap-0 ">
            <div className="flex h-fit items-center gap-6">
              <div>
                {isAnonymous ? (
                  <Avatar sx={{ bgcolor: '#000', width: 60, height: 60 }}>
                    <span className="text-2xl">A</span>
                  </Avatar>
                ) : (
                  <>
                    {companyLogo ? (
                      <Image
                        src={companyLogo}
                        alt={`${companyName} logo`}
                        height={60}
                        width={60}
                      />
                    ) : (
                      <Avatar sx={{ bgcolor: '#000', width: 60, height: 60 }}>
                        <span className="text-2xl">
                          {companyName && companyName[0]}
                        </span>
                      </Avatar>
                    )}
                  </>
                )}
              </div>
              <div>
                <div>
                  <p className="text-left text-xl font-medium sm:text-xl">
                    {title}
                  </p>
                  <p className="flex items-center text-left text-xs text-gray-400 sm:text-sm">
                    {isAnonymous ? (
                      'Anonymous'
                    ) : (
                      <button
                        type="button"
                        onClick={handleOpenCompanyProfile}
                        className="hover:underline"
                      >
                        {companyName}{' '}
                      </button>
                    )}
                    <BsDot className="text-left text-lg text-gray-400" />
                    {state}
                    {state && country && ','} {country}
                  </p>
                </div>
                <div className="items mt-2 flex flex-wrap items-center gap-1 whitespace-nowrap text-[10px]">
                  <span className="text-xs text-gray-400">
                    Posted {formattedPostedDate}
                  </span>
                  <div className="text-base text-gray-400">
                    <BsDot />
                  </div>
                  <span className="text-[8px] text-[#EB5757] md:text-xs">
                    {isApplicationClosed ? (
                      'Application Closed'
                    ) : (
                      <p>Closing {formattedDeadline}</p>
                    )}
                  </span>
                  <div className="text-base text-gray-400">
                    <BsDot />
                  </div>
                  <span className=" text-[8px] text-gray-400 md:text-xs">
                    {availableOpenings}{' '}
                    {availableOpenings && Number(availableOpenings) > 1
                      ? 'Openings'
                      : 'Opening'}
                  </span>
                  <div className="text-base text-gray-400">
                    <BsDot />
                  </div>
                  <span className="text-blue-400">
                    {applicants} Applicant
                    {applicants && applicants > 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex flex-col items-start justify-between">
              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  className="flex items-center gap-1 rounded-lg bg-[#16BF44] py-1 px-2 text-sm text-white"
                  onClick={handleOpenShareModal}
                >
                  <Share size="18" color="#ffff" />
                  Share
                </button>

                <button
                  type="button"
                  onClick={handleOpenReportModal}
                  className="flex items-center gap-1 rounded-lg border border-[#FF8A65] py-1 px-2 text-sm text-[#FF8A65]"
                >
                  <Dislike size="18" color="#FF8A65" />
                  Report
                </button>
              </div>
            </div>
          </div>

          <div className=" mt-10 grid gap-4 rounded-lg border border-[#E6E5E5] py-6 px-4  md:grid-cols-3 md:flex-row">
            <div className="border-r border-[#E6E5E5] px-2">
              <p className="whitespace-nowrap  text-xs capitalize">Location</p>
              <Tooltip title={jobLocation}>
                <p className=" mt-1 cursor-pointer truncate whitespace-nowrap text-sm font-medium">
                  {jobLocation}
                </p>
              </Tooltip>
              <hr className="my-2" />
              <p className="whitespace-nowrap  text-xs ">
                Minimum Qualification
              </p>
              <p className=" mt-1  whitespace-nowrap  text-sm font-medium ">
                {minQualification}
              </p>
            </div>
            <div className="border-r border-[#E6E5E5] px-2">
              <p className="whitespace-nowrap text-xs  ">Minimum Experience</p>
              <p className=" mt-1 whitespace-nowrap  text-sm font-medium">
                {level}
              </p>
              <hr className="my-2" />
              <p className="whitespace-nowrap  text-xs ">Experience Length</p>
              <p className=" mt-1  whitespace-nowrap  text-sm font-medium ">
                {experience}{' '}
              </p>
            </div>
            <div className=" px-2">
              <p className="whitespace-nowrap  text-xs ">Employment Type</p>
              <p className=" mt-1  whitespace-nowrap text-sm font-medium capitalize">
                {employmentType} ({workType})
              </p>
              <hr className="my-2" />
              <p className="whitespace-nowrap  text-xs ">Compensation</p>
              {hidden ? (
                <p className="mt-1 whitespace-nowrap text-sm font-medium capitalize">
                  Confidential
                </p>
              ) : (
                <>
                  <p className=" mt-1  whitespace-nowrap text-sm font-medium line-clamp-1 ">
                    {currency}
                    {addCommaToNumber(min || 0)}
                    {(max || 0) > 0 && (
                      <span>
                        {' - '}
                        {currency}
                        {addCommaToNumber(max || 0)}{' '}
                      </span>
                    )}{' '}
                    / <span>{term}</span>
                  </p>
                </>
              )}
            </div>
          </div>

          <div className="mt-5 border-b font-light">
            <h5 className="text-[16px] font-normal ">Job Summary</h5>
            <p className="mb-4 mt-2">{summary}</p>
          </div>
          <h5 className="pt-6 text-[16px] font-normal">
            Job Description/Requirements
          </h5>
          <div
            className="job-description mt-2 text-sm"
            dangerouslySetInnerHTML={{ __html: description || '' }}
          ></div>

          <hr className="my-8" />
          <Button onClick={handleApplyForJob}>
            Apply for this Job
          </Button>
          <div className="mb-10"></div>
        </div>
      </div>

      <LoginPopUp
        openLoginPopUp={openLoginPopUp}
        setOpenLoginPopUp={setOpenLoginPopUp}
        action="report this job"
      />

      <LoginPopUp
        openLoginPopUp={openLoginPopUpCompanyProfile}
        setOpenLoginPopUp={setOpenLoginPopUpCompanyProfile}
        action="view this company's profile"
      />

      <ShareJob
        openShareJob={openShareJob}
        setOpenShareJob={setOpenShareJob}
        jobId={jobId}
      />
    </>
  )
}
